import { Fragment, type ReactNode } from "react";
import { SAFE_LINK_PROTOCOLS } from "./constants";

/**
 * Minimal safe markdown → React renderer for assistant answers.
 * Supports paragraphs, line breaks, `###` / `####` headings, flat `-`/`*`/`1.` lists, **bold**,
 * *italic* / _italic_, `code`, [text](url) links and [n] citation markers. Never interprets HTML:
 * everything is a React text node, so `<script>` etc. render literally. Links outside
 * http(s)/mailto/tel/relative are dropped.
 *
 * It is fed the text of a streaming answer on every token, so half-written syntax renders
 * gracefully: an unclosed `**` is already bold, a lone trailing marker, a `[1` citation or the
 * start of a link (`[text](http`) never flashes as literal punctuation, and a `###` with no title
 * yet is held back.
 *
 * Typography lives here (semantic token utilities only) so the widget, web app and console
 * Playground render one look: line-height 1.5, paragraph spacing, indented lists with markers,
 * bold at 700 and headings a step above the body.
 */
export interface MarkdownOptions {
  renderCitation?: (n: number, key: string) => ReactNode;
  /**
   * `aria-level` of a `###` heading; `####` is one level deeper. Headings render as
   * `<p role="heading">` (never an `<h*>`), so an answer cannot disturb the host page's outline.
   * Default 3, which suits a panel under the page's `h1`/`h2`.
   */
  headingLevel?: number;
}

const DEFAULT_HEADING_LEVEL = 3;
const MAX_ARIA_HEADING_LEVEL = 6;

// eslint-disable-next-line no-control-regex -- stripping control chars is the point (defeats "java<TAB>script:" scheme smuggling).
const CONTROL_AND_SPACE = /[\u0000- \u007F]+/g;
const SAFE_PROTOCOLS: ReadonlySet<string> = new Set(SAFE_LINK_PROTOCOLS);

export function safeHref(raw: string): string | null {
  const cleaned = raw.replace(CONTROL_AND_SPACE, "");
  if (!cleaned) return null;
  if ((cleaned.startsWith("/") && !cleaned.startsWith("//")) || cleaned.startsWith("#")) return cleaned;
  try {
    const url = new URL(cleaned);
    return SAFE_PROTOCOLS.has(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

// Alternatives are tried in order at each position: the complete forms come first, the
// half-written (streaming) forms after them, so a finished `**x**` is never read as an open one.
const INLINE =
  /\\(?<esc>[<>])|\[(?<linkText>[^\]\n]+)\]\((?<href>[^)\s]+)\)|\[(?<cite>\d{1,2})\]|\*\*(?<bold>.+?)\*\*|`(?<code>[^`\n]+)`|(?<![\w*])\*(?<em>[^*\s](?:[^*\n]*[^*\s])?)\*(?!\*)|(?<![\w])_(?<em2>[^_\s](?:[^_\n]*[^_\s])?)_(?![\w])|\*\*(?<openBold>[^\n*][^\n]*)$|\[(?<partialLink>[^\]\n]+)\]\([^)\n]*$|(?<![\w*])\*{1,2}$|\[\d{0,2}$/g;

const LINK_CLASS = "font-semibold text-primary-ink underline underline-offset-2";
const CODE_CLASS = "rounded-xs bg-surface px-1 font-mono text-xs";
const STRONG_CLASS = "font-bold";

function renderInline(text: string, options: MarkdownOptions, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const match of text.matchAll(INLINE)) {
    const groups = match.groups ?? {};
    const index = match.index;
    if (index > last) out.push(text.slice(last, index));
    last = index + match[0].length;
    const key = `${keyPrefix}-${i++}`;
    if (groups.esc !== undefined) {
      // The service desk stores `<` and `>` of a message as `\<` and `\>` (HTML shown as text): the backslash is not for reading.
      out.push(groups.esc);
    } else if (groups.linkText !== undefined && groups.href !== undefined) {
      const href = safeHref(groups.href);
      out.push(
        href ? (
          <a key={key} href={href} target="_blank" rel="noopener noreferrer nofollow" className={LINK_CLASS}>
            {groups.linkText}
          </a>
        ) : (
          groups.linkText
        ),
      );
    } else if (groups.cite !== undefined) {
      const n = Number(groups.cite);
      out.push(options.renderCitation ? <Fragment key={key}>{options.renderCitation(n, key)}</Fragment> : match[0]);
    } else if (groups.bold !== undefined || groups.openBold !== undefined) {
      out.push(
        <strong key={key} className={STRONG_CLASS}>
          {renderInline(groups.bold ?? groups.openBold ?? "", options, key)}
        </strong>,
      );
    } else if (groups.code !== undefined) {
      out.push(
        <code key={key} className={CODE_CLASS}>
          {groups.code}
        </code>,
      );
    } else if (groups.partialLink !== undefined) {
      out.push(groups.partialLink);
    } else if (groups.em !== undefined || groups.em2 !== undefined) {
      out.push(<em key={key}>{renderInline(groups.em ?? groups.em2 ?? "", options, key)}</em>);
    }
    // Anything else is a dangling marker (`**`, `*`, `[1`) at the end of the text: dropped.
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export type Block =
  | { kind: "p"; lines: string[] }
  | { kind: "ul" | "ol"; items: string[] }
  | { kind: "h"; depth: 1 | 2; text: string };

const BULLET = /^\s*[-*•]\s+(.*)$/;
const ORDERED = /^\s*\d{1,3}[.)]\s+(.*)$/;
const HEADING = /^\s{0,3}(#{1,6})\s+(.+?)\s*#*\s*$/;
/** A heading marker or list dash that has arrived without its text yet (streaming). */
const PENDING_MARKER = /^\s{0,3}(?:#{1,6}|[-•*])\s*$/;
/** `#`, `##`, `###` are the answer's section headings; deeper ones are sub-headings. */
const SECTION_MAX_HASHES = 3;

export function parseBlocks(source: string): Block[] {
  const blocks: Block[] = [];
  let current = null as Block | null;
  for (const line of source.replace(/\r\n?/g, "\n").split("\n")) {
    const heading = HEADING.exec(line);
    const bullet = BULLET.exec(line);
    const ordered = bullet ? null : ORDERED.exec(line);
    const listKind = bullet ? "ul" : ordered ? "ol" : null;
    if (line.trim() === "" || PENDING_MARKER.test(line)) {
      current = null;
    } else if (heading) {
      const [, hashes = "", text = ""] = heading;
      blocks.push({ kind: "h", depth: hashes.length <= SECTION_MAX_HASHES ? 1 : 2, text });
      current = null;
    } else if (listKind) {
      const item = (bullet ?? ordered)?.[1] ?? "";
      if (current?.kind !== listKind) {
        current = { kind: listKind, items: [] };
        blocks.push(current);
      }
      current.items.push(item);
    } else if (current?.kind === "p") {
      current.lines.push(line);
    } else {
      current = { kind: "p", lines: [line] };
      blocks.push(current);
    }
  }
  return blocks;
}

const BLOCK_GAP = "[&:not(:first-child)]:mt-2.5";
const PARAGRAPH_CLASS = `${BLOCK_GAP} leading-normal`;
const LIST_BASE = `${BLOCK_GAP} space-y-1 pl-5 leading-normal marker:text-fg-subtle`;
const BULLETS_CLASS = `${LIST_BASE} list-disc`;
const NUMBERS_CLASS = `${LIST_BASE} list-decimal marker:font-semibold`;
const HEADING_CLASSES = {
  1: "[&:not(:first-child)]:mt-4 text-[15px] font-bold leading-snug tracking-tight text-fg",
  2: "[&:not(:first-child)]:mt-3 text-sm font-bold leading-snug text-fg-muted",
} as const;

function headingLevel(options: MarkdownOptions, depth: 1 | 2): number {
  const base = Math.min(Math.max(options.headingLevel ?? DEFAULT_HEADING_LEVEL, 1), MAX_ARIA_HEADING_LEVEL);
  return Math.min(base + depth - 1, MAX_ARIA_HEADING_LEVEL);
}

export function renderMarkdown(source: string, options: MarkdownOptions = {}): ReactNode[] {
  return parseBlocks(source).map((block, b) => {
    const key = `b${b}`;
    if (block.kind === "h") {
      return (
        <p key={key} role="heading" aria-level={headingLevel(options, block.depth)} className={HEADING_CLASSES[block.depth]}>
          {renderInline(block.text, options, key)}
        </p>
      );
    }
    if (block.kind === "p") {
      return (
        <p key={key} className={PARAGRAPH_CLASS}>
          {block.lines.map((line, l) => (
            <Fragment key={l}>
              {l > 0 ? <br /> : null}
              {renderInline(line, options, `${key}-${l}`)}
            </Fragment>
          ))}
        </p>
      );
    }
    const List = block.kind;
    return (
      <List key={key} className={List === "ul" ? BULLETS_CLASS : NUMBERS_CLASS}>
        {block.items.map((item, i) => (
          <li key={i}>{renderInline(item, options, `${key}-${i}`)}</li>
        ))}
      </List>
    );
  });
}

/** Plain text for copy / aria / transcripts: markdown syntax and citation markers removed. */
export function markdownToPlainText(source: string): string {
  return source
    .replace(/\[(\d{1,2})\]/g, "")
    .replace(/^[ \t]{0,3}#{1,6}[ \t]+(.+?)[ \t#]*$/gm, "$1")
    .replace(/\[([^\]\n]+)\]\(([^)\s]+)\)/g, "$1")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/`([^`\n]+)`/g, "$1")
    .replace(/(^|[^\w*])\*([^*\n]+)\*/g, "$1$2")
    .replace(/^[ \t]*[-*•][ \t]+/gm, "• ")
    .replace(/[ \t]+$/gm, "")
    .trim();
}

export function Markdown({ source, ...options }: MarkdownOptions & { source: string }) {
  return <>{renderMarkdown(source, options)}</>;
}
